<template>
  <div class="container">
    <Toolbar class="mt-2 mb-2">
      <template #start>
        <h2>
          <span v-if="isUpdate">
            {{ $t('userpage.updateUser') }}
          </span>
          <span v-else>{{ $t('userpage.createUser') }}</span>
        </h2>
      </template>
    </Toolbar>
    <form
      v-if="!success"
      @submit.prevent="validForm"
    >
      <div class="p-input mb-3">
        <label
          for="stakeholder"
          :class="{ 'required-input' : user.role === contactRole}"
        >{{
          $t('entity.user.stakeholderId') }}</label>
        <InputText
          id="stakeholder"
          v-model.trim="v$.user.stakeholderId.$model"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.user.stakeholderId.$error }"
          type="text"
          autocomplete="on"
        />
        <div
          v-for="error of v$.user.stakeholderId.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div class="p-input mb-3">
        <label
          for="lastname"
          class="required-input"
        >{{ $t('entity.user.lastname') }}</label>
        <InputText
          id="lastname"
          v-model="v$.user.lastname.$model"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.user.lastname.$error }"
          type="text"
          autocomplete="family-name"
          required
        />
        <div
          v-for="error of v$.user.lastname.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div class="p-input mb-3">
        <label
          for="firstname"
          class="required-input"
        >{{ $t('entity.user.firstname') }}</label>
        <InputText
          id="firstname"
          v-model="v$.user.firstname.$model"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.user.firstname.$error }"
          type="text"
          autocomplete="given-name"
          required
        />
        <div
          v-for="error of v$.user.firstname.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div class="p-input mb-3">
        <label
          for="company"
          :class="{ 'required-input' : user.role === contactRole}"
        >{{ $t('entity.user.company')
        }}</label>
        <InputText
          id="company"
          v-model="v$.user.company.$model"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.user.company.$error }"
          type="text"
          autocomplete="organization"
        />
        <div
          v-for="error of v$.user.company.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div class="p-input mb-3">
        <label
          for="email-register"
          class="required-input"
        >{{ $t('entity.user.email') }}</label>
        <InputText
          id="email-register"
          v-model="v$.user.email.$model"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.user.email.$error }"
          type="email"
          autocomplete="email"
          required
        />
        <div
          v-for="error of v$.user.email.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div class="p-input mb-3">
        <label for="phoneNumber">{{ $t('entity.user.phoneNumber') }}</label>
        <InputPhoneNumber
          id="phoneNumber"
          v-model:phoneNumberCountryCode="v$.user.phoneNumberCountryCode.$model"
          v-model:phoneNumber="v$.user.phoneNumber.$model"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.user.phoneNumber.$error }"
        />
        <div
          v-for="error of v$.user.phoneNumber.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div
        v-if="isAdmin"
        class="p-input mb-3"
      >
        <label
          for="role"
          class="required-input"
        >{{ $t('entity.user.role') }}</label>
        <Dropdown
          id="role"
          v-model="v$.user.role.$model"
          :placeholder="$t('userpage.form.role.placeholder')"
          :options="roles"
          :option-label="(role) => $t(role.label)"
          option-value="value"
          class="col-12 p-0 mt-2"
          :class="{'invalid-input': v$.user.role.$error }"
          :disabled="currentUser"
        />
        <div
          v-for="error of v$.user.role.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div class="p-input mb-3">
        <label for="yubekey">{{ $t('entity.user.yubekey') }}</label>
        <InputText
          id="yubekey"
          v-model="v$.user.yubekey.$model"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.user.yubekey.$error }"
          type="text"
        />
        <div
          v-for="error of v$.user.yubekey.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div
        v-if="!isAutoApproval && isAdmin"
        class="p-input mb-3 field-checkbox"
      >
        <Checkbox
          id="approver"
          v-model="v$.user.approver.$model"
          :binary="true"
          :class="{'invalid-input': v$.user.approver.$error }"
          type="text"
          @change="user.stakeholdersApprover = []"
        />
        <label for="approver">{{ $t('entity.user.approver') }}</label>
      </div>

      <div
        v-if="!isAutoApproval && isAdmin && user.approver"
        class="p-input mb-3"
      >
        <label for="stakeholdersApprover">{{ $t('userpage.form.stakeholdersApprover') }}</label>
        <MultiSelect
          id="stakeholdersApprover"
          v-model="v$.user.stakeholdersApprover.$model"
          :options="stakeholders"
          :option-label="(stakeholder) => stakeholder.stakeholderId"
          option-value="stakeholderId"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.user.stakeholdersApprover.$error }"
          :virtual-scroller-options="{ itemSize: 44 }"
          type="text"
          display="chip"
          filter
        />
      </div>

      <Message
        v-if="errors"
        severity="error"
        :closable="false"
      >
        <ul class="list-none m-0 p-0">
          <li
            v-for="error of errors"
            :key="error"
          >
            {{ $t(error) }}
          </li>
        </ul>
      </Message>
      <div class="my-3 flex justify-content-between flex-wrap">
        <Button
          :label="$t('action.back')"
          icon="pi pi-arrow-left"
          class="p-button-secondary p-button-rounded p-button-outlined"
          @click="toUserTable"
        />
        <Button
          type="submit"
          :label="isUpdate ? $t('action.crud.update') : $t('action.crud.create')"
          icon="pi pi-save"
          class="p-button-rounded p-button-outlined"
          :disabled="saveButtonDisabled"
          :loading="isSaving"
        />
      </div>
    </form>
  </div>
</template>

<script>
import {useVuelidate} from "@vuelidate/core";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import Message from "primevue/message";
import Dropdown from 'primevue/dropdown';
import Checkbox from "primevue/checkbox";
import MultiSelect from "primevue/multiselect";
import {email, phoneNumberPattern, required, requiredIf} from "@/i18n/i18nValidators";
import {ADMIN_ROLE, AGENT_ROLE, CONTACT_ROLE} from "@/shared/constants";
import Toolbar from "primevue/toolbar";
import useStakeholderFilter from "@/composables/filters/useStakeholderFilter";
import {reactive} from "vue";
import InputPhoneNumber from "@/components/InputPhoneNumber.vue";

export default {
  name: 'UserForm',
  components: {InputPhoneNumber, InputText, Button, Message, Dropdown, Toolbar, Checkbox, MultiSelect },
  setup() {
    const stakeholderFilter = reactive(useStakeholderFilter())
    stakeholderFilter.pageable.size = 1000;
    return {
      stakeholderFilter,
      v$: useVuelidate()
    }
  },
  data() {
    return {
      user: {
        id: null,
        stakeholderId: '',
        lastname: '',
        firstname: '',
        company: '',
        email: '',
        phoneNumberCountryCode: 'FR',
        phoneNumber: '',
        role: null,
        yubekey: null,
        approver: false,
        stakeholdersApprover: []
      },
      stakeholders: [],
      roles: [
        { value: CONTACT_ROLE, label: 'entity.user.roles.contact' },
        { value: AGENT_ROLE, label: 'entity.user.roles.agent' },
        { value: ADMIN_ROLE, label: 'entity.user.roles.admin' }
      ],
      isUpdate: false,
      errors: null,
      success: false,
      isSaving: false
    }
  },
  validations() {
    return {
      user: {
        stakeholderId: {
          required: requiredIf(function () {
            return this.user.role === CONTACT_ROLE;
          })
        },
        lastname: { required },
        firstname: { required },
        company: {
          required: requiredIf(function () {
            return this.user.role === CONTACT_ROLE;
          })
        },
        email: { required, email },
        phoneNumberCountryCode: {},
        phoneNumber: { phoneNumberPattern: phoneNumberPattern(this.user.phoneNumberCountryCode)},
        role: { required },
        yubekey: {},
        approver: { required },
        stakeholdersApprover: {}
      }
    }
  },
  computed: {
    contactRole() {
      return CONTACT_ROLE;
    },
    currentUser() {
      return this.user.id === this.$store.state.auth.user.id
    },
    isAdmin() {
      return this.$store.getters['auth/userIsAdmin'];
    },
    saveButtonDisabled() {
      return this.v$.$invalid;
    },
    isAutoApproval() {
      return this.$store.getters['setting/isAutoApproval'];
    }
  },
  async mounted() {
    if (this.$route.params.id) {
      this.fetchUser(this.$route.params.id);
      this.isUpdate = true;
    }
    if(this.isAdmin) {
      const result = await this.$stakeHolderService.fetchStakeholdersPagingAndSorting(this.stakeholderFilter.getQueryParams());
      this.stakeholders = result.content;
    }
  },
  methods: {
    async fetchUser() {
      this.user = await this.$userService.fetchUser(this.$route.params.id);
      this.user.stakeholderId = this.user.stakeholder?.stakeholderId
      this.user.stakeholdersApprover = this.user.stakeholdersApprover?.map(stakeholder => stakeholder.stakeholderId);
    },
    async validForm() {
      this.errors = null;
      const isFormValid = await this.v$.$validate();
      if (!isFormValid) return;

      this.isSaving = true;

      if (this.user.id) { // Création d'un utilisateur
        await this.updateUser()
      } else {
        await this.createUser()
      }
    },
    async createUser() {
      try {
        const created = await this.$userService.createUser(this.user)
        if (created) {
          this.success = true;
          this.$emitter.emit('toast', { severity: 'success', message: 'userpage.createSuccess' })
          this.errors = null;
          this.isSaving = false;
          this.toUserTable();
        }
      } catch (err) {
        this.success = false;
        this.errors = err.response.data;
        this.isSaving = false;
      }
    },
    async updateUser() {
      try {
        const updated = await this.$userService.updateUser(this.user)
        if (updated) {
          this.success = true;
          this.$emitter.emit('toast', { severity: 'success', message: 'userpage.updateSuccess' })
          this.errors = null;
          this.isSaving = false;
          this.toUserTable();
        }
      } catch (err) {
        this.success = false;
        this.errors = err.response.data;
        this.isSaving = false;
      }
    },
    toUserTable() {
      this.$router.push({ name: 'user' })
    }
  }
}
</script>

<style lang="scss" scoped>

</style>
